import { ActionType, createAsyncAction } from 'typesafe-actions';
import { FetchAction, SuccessFetchActionPayload, DefaultErrorPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { PriceFormatConfigOptions } from '../products/types';
import { addNotification } from '../notifications/actions';
import { remoteUpateCartEvent } from '../../../common/events/service';
import { NavigationPush } from '../../hooks/useBellaHistoryPush';
import { getAddonProductsUrl } from '../../urlService';
import { ProductDetail } from '../productdetail/types';
import { DisplayType } from '../notifications/types';
import { CartCountResponse, CartResponse } from './types';

export const fetchCartCountAction = createAsyncAction(
    '@CART_DATA/FETCH_START',
    '@CART_DATA/FETCH_SUCCESS',
    '@CART_DATA/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<CartCountResponse>, DefaultErrorPayload>();

export const fetchCartCount = (): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    const identifier = 'CART_COUNT';
    dispatch(fetchCartCountAction.request({ identifier }));
    try {
        const cartCount = await apiService.shopApi.getCartCount();
        dispatch(fetchCartCountAction.success({ data: cartCount, identifier }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchCartCount');
        dispatch(fetchCartCountAction.failure({ error: err, identifier }));
    }
};

export const addToCartAction = createAsyncAction(
    '@CART_DATA_ADD_TO_CART/FETCH_START',
    '@CART_DATA_ADD_TO_CART/FETCH_SUCCESS',
    '@CART_DATA_ADD_TO_CART/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<{}>, DefaultErrorPayload>();

export const fetchCartDataAction = createAsyncAction('@CART/FETCH_START', '@CART/FETCH_SUCCESS', '@CART/FETCH_FAILED')<
    FetchAction,
    SuccessFetchActionPayload<CartResponse>,
    DefaultErrorPayload
>();

export const fetchCartData = (reducerPayloadOptions: PriceFormatConfigOptions): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    const identifier = 'CART_DATA';
    dispatch(fetchCartDataAction.request({ identifier }));
    try {
        const cartCount = await apiService.shopApi.getCartData();
        dispatch(fetchCartDataAction.success({ data: cartCount, identifier, reducerPayloadOptions }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchCartData');
        dispatch(fetchCartDataAction.failure({ error: err, identifier }));
    }
};

export interface AddToCartOptions {
    shouldShowCartFlyout?: boolean;
    hasAddonProducts?: boolean;
    successNotification?: string;
    errorNotification: string;
    product: ProductDetail;
    quantity: number;
}

export const addToCart = (navigationPush: NavigationPush, options: AddToCartOptions): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    const updateCart = remoteUpateCartEvent();
    const { product, quantity } = options;
    const simpleSku = product.activeSimpleSKU;
    const identifier = simpleSku;
    try {
        dispatch(addToCartAction.request({ identifier }));
        await apiService.shopApi.addToCart(simpleSku, quantity);
        dispatch(addToCartAction.success({ data: {}, identifier }));

        if (product.hasAddonProducts) {
            const configSku = product.sku;
            navigationPush(getAddonProductsUrl(configSku, simpleSku));
            return;
        }

        // The preferable way to communicate with the One Header (Micro Frontend) is remote events. We should treat it as a separate application.
        updateCart(!!options.shouldShowCartFlyout);
        if (options.successNotification) {
            dispatch(addNotification(options.successNotification));
        }
    } catch (err) {
        dispatch(addToCartAction.failure({ error: err as Error, identifier }));
        log404ErrorAsWarning(err, 'ADD_TO_CART', logger, 'addToCart');
        if (options.errorNotification) {
            dispatch(addNotification(options.errorNotification, null, DisplayType.error));
        }
    }
};

export type CartActionType = ActionType<
    typeof fetchCartDataAction | typeof fetchCartCountAction | typeof addToCartAction
>;
